import React from 'react';
import { isNil } from 'lodash';
import OpenOrderItemDetail from '../../../../../../components/link/OpenOrderItemDetail';
import OpenProductDetail from '../../../../../../components/link/OpenProductDetail';
import OpenPreviewProductDetail from '../../../../../../containers/app/common/OpenPreviewProductDetail';
import Anchor from '../../../../../../components/link/Anchor';
import TableColumnTitle from '../../../../../../components/_table/TableColumnTitle';
import OpenBrandDetail from '../../../../../../components/link/OpenBrandDetail';
import TableFilterSelect from '../../../../../../components/_table/TableFilterSelect';
import {
  asyncSimpleModal,
  setDash,
  setNumberWithComma,
  setPhoneFormat,
  addAllItemToSelectList,
} from '../../../../../../lib/common';
import { setDateFormat } from '../../../../../../lib/moment';
import { setBuyerAccount, setOrderMessage } from '../../../../../../lib/utils/orderutils';
import { DATE_FORMAT } from '../../../../../../constants/service/common/utilConstants';
import {
  ORDER_ITEM_STATUS_TEXT,
  ORDER_CANCEL_ITEM_STATUS_TEXT,
  ORDER_ACCEPT_MANAGER_TEXT,
  ORDER_PROCESS_MANAGER_TEXT,
  PAY_METHOD_TYPE_TEXT,
  ORDER_TYPE_SELECT_LIST,
  ORDER_TYPE_TEXT,
  ORDER_GIFT_STATUS_SELECT_LIST,
  ORDER_GIFT_STATUS_TEXT,
  ORDER_CANCEL_TYPE_TEXT,
} from '../../../../../../constants/service/app/orderConstants';
import { PRODUCT_MANAGEMENT_TYPE_TEXT } from '../../../../../../constants/service/app/productConstants';
import { BRAND_TYPE_TEXT } from '../../../../../../constants/service/app/brandConstants';

const columns = (data) => {
  const { filterOption, onFilter } = data;

  return [
    {
      title: <TableColumnTitle title="주문 타입" filterValue={ORDER_TYPE_TEXT[filterOption.orderType]} />,
      dataIndex: 'orderType',
      filterDropdown: ({ confirm }) => (
        <TableFilterSelect
          list={addAllItemToSelectList(ORDER_TYPE_SELECT_LIST)}
          option={filterOption.orderType}
          confirm={confirm}
          onFilter={(orderType) => onFilter({ orderType })}
        />
      ),
      render: (value) => setDash(ORDER_TYPE_TEXT[value]),
    },
    {
      title: <TableColumnTitle title="선물 상태" filterValue={ORDER_GIFT_STATUS_TEXT[filterOption.giftStatus]} />,
      dataIndex: 'giftStatus',
      filterDropdown: ({ confirm }) => (
        <TableFilterSelect
          list={addAllItemToSelectList(ORDER_GIFT_STATUS_SELECT_LIST)}
          option={filterOption.giftStatus}
          confirm={confirm}
          onFilter={(giftStatus) => onFilter({ giftStatus })}
        />
      ),
      render: (value) => setDash(ORDER_GIFT_STATUS_TEXT[value]),
    },
    {
      title: <TableColumnTitle className="primary" title={'취소 정보'} />,
      children: [
        {
          title: '주문번호',
          dataIndex: 'orderId',
        },
        {
          title: '상품 주문번호',
          dataIndex: 'orderItemId',
          render: (value) => <OpenOrderItemDetail text={value} orderItemId={value} />,
        },
        {
          title: '주문 메시지',
          dataIndex: 'orderMessage',
          render: (value) => setOrderMessage(value),
        },
        {
          title: '스타일코드(바코드)',
          dataIndex: 'productStyleCode',
          render: (value) => setDash(value),
        },
        {
          title: '바코드',
          dataIndex: 'productOptionStyleCode',
          render: (value) => setDash(value),
        },
        {
          title: '주문 상태',
          dataIndex: 'orderItemStatus',
          render: (value) => ORDER_ITEM_STATUS_TEXT[value],
        },
        {
          title: '취소 상태',
          dataIndex: 'orderItemCancelStatus',
          render: (value) => ORDER_CANCEL_ITEM_STATUS_TEXT[value],
        },
        {
          title: '접수자',
          dataIndex: 'acceptManager',
          render: (value) => ORDER_ACCEPT_MANAGER_TEXT[value],
        },
        {
          title: '취소 처리자',
          dataIndex: 'cancelManager',
          render: (value) => ORDER_PROCESS_MANAGER_TEXT[value],
        },
        {
          title: '취소사유',
          dataIndex: 'cancelType',
          render: (value, record) => (
            <Anchor
              color="primary"
              isUnderLine={true}
              text={ORDER_CANCEL_TYPE_TEXT[value]}
              onClick={async () =>
                await asyncSimpleModal({
                  type: 'info',
                  title: '취소사유',
                  message: record.cancelReason,
                })
              }
            />
          ),
        },
        {
          title: '실제 고객 환불 금액',
          dataIndex: 'refundActualAmount',
          render: (value) => setDash(setNumberWithComma(value)),
        },
      ],
    },
    {
      title: <TableColumnTitle className="primary" title={'취소 거부'} />,
      children: [
        {
          title: '거부 사유',
          dataIndex: 'rejectReason',
          render: (value, record) =>
            !isNil(value) ? (
              <Anchor
                color="primary"
                isUnderLine={true}
                text="내용 보기"
                onClick={async () =>
                  await asyncSimpleModal({
                    type: 'info',
                    title: '취소 거부 사유',
                    message: record.rejectReason,
                  })
                }
              />
            ) : (
              '-'
            ),
        },
        {
          title: '거부 일시',
          dataIndex: 'rejectDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
      ],
    },
    {
      title: <TableColumnTitle className="primary" title={'주문건 처리 일시'} />,
      children: [
        {
          title: '주문 일시',
          dataIndex: 'orderDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
        {
          title: '결제 일시',
          dataIndex: 'paymentDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
        {
          title: '취소 요청 일시',
          dataIndex: 'canceledDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
        {
          title: '취소 완료 일시',
          dataIndex: 'cancelCompleteDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
      ],
    },
    {
      title: <TableColumnTitle className="primary" title={'구매자/수취인'} />,
      children: [
        {
          title: '구매자ID',
          dataIndex: 'buyerAccount',
          render: (value) => setBuyerAccount(value),
        },
        {
          title: '구매자명',
          dataIndex: 'buyerName',
        },
        {
          title: '수취인명',
          dataIndex: 'recipientName',
        },
      ],
    },
    {
      title: <TableColumnTitle className="primary" title={'구매상품 정보'} />,
      children: [
        {
          title: '브랜드명',
          dataIndex: 'brandName',
          render: (value, record) => <OpenBrandDetail text={value} storeId={record.storeId} />,
        },
        {
          title: '브랜드 ID',
          dataIndex: 'storeId',
          render: (value) => <OpenBrandDetail text={value} storeId={value} />,
        },
        {
          title: '상품번호',
          dataIndex: 'productId',
          render: (value) => <OpenProductDetail text={value} productId={value} />,
        },
        {
          title: '제품번호',
          dataIndex: 'productNumber',
          render: (value) => setDash(value),
        },
        {
          title: '관리처',
          dataIndex: 'store',
          render: (value) => setDash(BRAND_TYPE_TEXT[value]),
        },
        {
          title: '타입',
          dataIndex: 'productType',
          render: (value) => setDash(PRODUCT_MANAGEMENT_TYPE_TEXT[value]),
        },
        {
          title: '상품명',
          dataIndex: 'productName',
          render: (value, record) => <OpenPreviewProductDetail text={value} productId={record.productId} />,
        },
        {
          title: '1차 카테고리',
          dataIndex: 'mainCategory',
        },
        {
          title: '옵션정보',
          dataIndex: 'productOption',
          render: (value) => setDash(value),
        },
        {
          title: '옵션 제품번호',
          dataIndex: 'productOptionNumber',
          render: (value) => setDash(value),
        },
        {
          title: '수량',
          dataIndex: 'quantity',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '상품 가격(정가)',
          dataIndex: 'productPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '상품 할인액(차감된 금액)',
          dataIndex: 'discountPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '옵션가격',
          dataIndex: 'productOptionPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '쿠폰',
          dataIndex: 'usedCoupon',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: 'Apoint',
          dataIndex: 'usedPoint',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '상품 별 총 주문금액',
          dataIndex: 'totalPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '추가 배송비',
          dataIndex: 'deliveryExtraFee',
          render: (value) => setNumberWithComma(value),
        },
      ],
    },
    {
      title: <TableColumnTitle className="primary" title={'배송 수취 정보'} />,
      children: [
        {
          title: '구매자 연락처',
          dataIndex: 'buyerPhone',
          render: (value) => setPhoneFormat(value),
        },
        {
          title: '수취인 연락처',
          dataIndex: 'recipientPhone',
          render: (value) => setPhoneFormat(value),
        },
        {
          title: '배송지',
          dataIndex: 'shippingAddress',
          render: (value, record) =>
            record.shippingAddress1 &&
            record.shippingAddress2 &&
            `${record.shippingAddress1} ${record.shippingAddress2}`,
        },
        {
          title: '우편번호',
          dataIndex: 'shippingPostCode',
        },
        {
          title: '배송메시지',
          dataIndex: 'shippingMessage',
          render: (value) => setDash(value),
        },
      ],
    },
    {
      title: <TableColumnTitle className="primary" title={'결제 정보'} />,
      children: [
        {
          title: '결제 수단',
          dataIndex: 'payMethod',
          render: (value) => PAY_METHOD_TYPE_TEXT[value],
        },
      ],
    },
  ];
};

export default columns;
